import { type JsxElementProps, jsx } from '@amazon/vinyl-tsx'
import type { JsxChild } from '@amazon/vinyl-tsx'
import type { MaybeObservableValue } from '@amazon/vinyl-observable'

/**
 * Hyphenated `aria-` / `data-` pass-throughs. Intrinsic JSX elements accept
 * hyphenated attributes via TS's built-in escape hatch, but a component's prop
 * type does not — so a shared wrapper must declare them explicitly to keep the
 * same call shape (`aria-label`, `data-testid`, …) callers use on a raw
 * `<button>`.
 */
type ForwardedAttrs = {
    readonly [K in `aria-${string}` | `data-${string}`]?: MaybeObservableValue<
        string | number | boolean | null
    >
}

export type ButtonProps = JsxElementProps<'button'> & ForwardedAttrs

/**
 * Shared `<button>` primitive that defaults `tabIndex` to 0 so WebKit (Safari,
 * under its default keyboard prefs, which drops untabindexed native controls
 * from the sequential Tab order) keeps the button reachable. A caller-passed
 * tabIndex — including -1 to opt a control out — overrides the default. Carries
 * no visuals of its own; className / onclick / disabled / aria-* / children all
 * pass straight through.
 */
export function Button({ tabIndex, ...rest }: ButtonProps, children: readonly JsxChild[]) {
    return (
        <button {...rest} tabIndex={tabIndex ?? 0}>
            {...children}
        </button>
    )
}
